// Descripción de Instagram de una imagen generada — webhook n8n
// `descripcion_instagram` migrado a atlas `/v1/images/instagram-description`
// (ADR-605). Flag ADENTRO del helper:
//   ON  → atlas con JWT (`user_id` NO viaja) + modo_utilizado
//         'descripcion_instagram'. La respuesta además trae
//         `post_description` directa → los callers la usan y se saltean el
//         poll de la fila (que queda como fallback).
//   OFF → webhook n8n byte-idéntico (solo {response:'success'}; el caller
//         pollea la fila como siempre).

import { GENERATE_AUTH_TOKEN, USE_ATLAS_BACKEND } from '../config';
import { atlasBackendFetch } from './atlasBackend';
import { supabase } from './supabase';

const DESCRIPCION_INSTAGRAM_URL =
  'https://ai-agency-dt7d.onrender.com/webhook/descripcion_instagram';

export interface InstagramDescriptionResult {
  ok: boolean;
  /** Solo del atlas: la descripción ya resuelta (skip del poll). */
  postDescription?: string;
}

export async function postInstagramDescription(body: {
  user_id: string;
  image_id: string | number;
}): Promise<InstagramDescriptionResult> {
  if (USE_ATLAS_BACKEND) {
    const { user_id: _ignored, ...atlasBody } = body;
    const res = await atlasBackendFetch('/v1/images/instagram-description', {
      ...atlasBody,
      modo_utilizado: 'descripcion_instagram',
    });
    const data = (await res.json().catch(() => null)) as {
      response?: string;
      post_description?: string;
    } | null;
    return {
      ok: data?.response === 'success',
      postDescription:
        typeof data?.post_description === 'string' &&
        data.post_description.trim()
          ? data.post_description
          : undefined,
    };
  }
  const res = await fetch(DESCRIPCION_INSTAGRAM_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: GENERATE_AUTH_TOKEN,
    },
    body: JSON.stringify(body),
  });
  const data = (await res.json().catch(() => null)) as {
    response?: string;
  } | null;
  return { ok: data?.response === 'success' };
}

/**
 * Espera a que la fila tenga `post_description`, polleando —
 * ADR-2026-09-08-la-descripcion-se-escribe-en-el-panel-del-modo.
 *
 * Es el fallback del camino n8n (flag OFF), que sólo contesta `{response:'success'}` y
 * escribe la descripción de manera asincrónica: sin este poll el caller se queda con la
 * respuesta vacía. Con el atlas prendido no se llama nunca —la descripción viene en el
 * cuerpo—, pero mientras el flag pueda volver a apagarse tiene que existir.
 *
 * `seguirEsperando` corta la espera cuando el llamador ya no la quiere (se desmontó, o
 * el usuario cambió de imagen): sin eso, un poll de 15s puede terminar pisando el
 * estado de una imagen que ya no es la que se está mirando.
 */
export async function esperarPostDescription({
  imageId,
  companyId,
  seguirEsperando,
  maxWaitMs = 15_000,
  intervalMs = 800,
}: {
  imageId: string | number;
  companyId: string;
  seguirEsperando?: () => boolean;
  maxWaitMs?: number;
  intervalMs?: number;
}): Promise<string | null> {
  const limite = Date.now() + maxWaitMs;
  while (Date.now() < limite) {
    if (seguirEsperando && !seguirEsperando()) return null;
    try {
      const { data } = await supabase
        .from('imagenes_generadas')
        .select('post_description')
        .eq('id', imageId)
        .eq('id_company', companyId)
        .single();
      const texto = (data as { post_description?: string | null } | null)
        ?.post_description;
      if (texto && texto.trim()) return texto;
    } catch {
      /* reintenta hasta el límite */
    }
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return null;
}
